import React, { useState, useEffect } from 'react';
import { X, Loader2, ChevronDown, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Checkbox } from '../ui/Checkbox';
import { apiRequest } from '../../services/apiClient';
import toast from 'react-hot-toast';

/**
 * TenantPermissionsModal Component
 * Centered modal for managing tenant screen permissions
 * 
 * @param {Boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Object} tenant - Tenant object with tenantId
 */
const TenantPermissionsModal = ({ isOpen, onClose, tenant }) => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [screenMappings, setScreenMappings] = useState({}); // { screenId: { isActive, mappingId, moduleId } }
  const [hasChanges, setHasChanges] = useState(false);
  const [initialMappings, setInitialMappings] = useState({});

  useEffect(() => {
    if (isOpen && tenant?.tenantId) {
      fetchTenantPermissions();
      setExpandedModules(new Set());
      setScreenMappings({});
      setInitialMappings({});
      setHasChanges(false);
    } else {
      setMenuData([]);
      setExpandedModules(new Set());
      setScreenMappings({});
      setInitialMappings({});
      setHasChanges(false);
    }
  }, [isOpen, tenant]);

  const fetchTenantPermissions = async () => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID is required');
      return;
    }

    setLoading(true);
    try {
      const orgId = tenant.tenantId;
      
      // Step 1: Fetch all available modules/screens (using existing API)
      const roleId = 1; // Super Admin role to show all permissions
      const menuResponse = await apiRequest(
        `/gateway/registration/userrolescreenpermission/get-menus/${roleId}/${orgId}`,
        { method: 'GET' }
      );

      //console.log('📦 Menu permissions response:', menuResponse);

      // Handle response structure
      let menus = [];
      if (Array.isArray(menuResponse)) {
        menus = menuResponse;
      } else if (menuResponse?.data && Array.isArray(menuResponse.data)) {
        menus = menuResponse.data;
      } else if (menuResponse?.response && Array.isArray(menuResponse.response)) {
        menus = menuResponse.response;
      } else if (menuResponse?.data?.data && Array.isArray(menuResponse.data.data)) {
        menus = menuResponse.data.data;
      } else if (menuResponse?.data?.response && Array.isArray(menuResponse.data.response)) {
        menus = menuResponse.data.response;
      } else if (menuResponse?.status === 'success' && Array.isArray(menuResponse.data)) {
        menus = menuResponse.data;
      }

      setMenuData(menus);
      
      // Step 2: Fetch existing tenant screen mappings (type='S')
      let existingMappings = [];
      try {
        const mappingResponse = await apiRequest(
          `/gateway/api/ticket/tenant-mapping/org/${orgId}/type/S`,
          { method: 'GET' }
        );

        //console.log('📦 Tenant screen mappings response:', mappingResponse);

        // Handle response structure
        if (Array.isArray(mappingResponse)) {
          existingMappings = mappingResponse;
        } else if (mappingResponse?.data && Array.isArray(mappingResponse.data)) {
          existingMappings = mappingResponse.data;
        } else if (mappingResponse?.data?.data && Array.isArray(mappingResponse.data.data)) {
          existingMappings = mappingResponse.data.data;
        }

        // Create a map of existing mappings: { featureScreenId: { id, isActive, moduleId } }
        const mappingsMap = {};
        existingMappings.forEach(mapping => {
          const screenId = mapping.featureScreenId || mapping.feature_screen_id;
          if (screenId) {
            mappingsMap[screenId] = {
              id: mapping.id,
              isActive: mapping.isActive !== false,
              moduleId: mapping.moduleId || mapping.module_id
            };
          }
        });

        // Step 3: Initialize screen mappings state from API
        const screenMappingsMap = {};
        menus.forEach(module => {
          const moduleId = module.moduleId || module.module_id;
          const subMenus = module.subMenu || module.sub_menu || [];
          subMenus.forEach(screen => {
            const screenId = screen.screen_id || screen.screenId;
            if (screenId) {
              const existingMapping = mappingsMap[screenId];
              screenMappingsMap[screenId] = {
                isActive: existingMapping ? existingMapping.isActive : false,
                mappingId: existingMapping ? existingMapping.id : null,
                moduleId: moduleId
              };
            }
          });
        });

        setScreenMappings(screenMappingsMap);
        setInitialMappings(JSON.parse(JSON.stringify(screenMappingsMap))); // Deep copy
      } catch (mappingError) {
        console.warn('Could not fetch existing mappings, using default state:', mappingError);
        // If mapping API fails, initialize with all screens unchecked
        const screenMappingsMap = {};
        menus.forEach(module => {
          const moduleId = module.moduleId || module.module_id;
          const subMenus = module.subMenu || module.sub_menu || [];
          subMenus.forEach(screen => {
            const screenId = screen.screen_id || screen.screenId;
            if (screenId) {
              screenMappingsMap[screenId] = {
                isActive: false,
                mappingId: null,
                moduleId: moduleId
              };
            }
          });
        });
        setScreenMappings(screenMappingsMap);
        setInitialMappings(JSON.parse(JSON.stringify(screenMappingsMap)));
      }
      
      // Keep all modules collapsed by default - user can expand as needed
      setExpandedModules(new Set());

      if (menus.length === 0) {
        toast('No permissions found for this tenant', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Error fetching tenant permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to load tenant permissions');
      setMenuData([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const toggleScreen = (screenId, value) => {
    setScreenMappings(prev => {
      const currentValue = prev[screenId]?.isActive || false;
      const newValue = value !== undefined ? value : !currentValue;
      
      const updated = {
        ...prev,
        [screenId]: {
          ...(prev[screenId] || {}),
          isActive: newValue,
        }
      };
      
      // Check if there are changes
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(initialMappings);
      setHasChanges(hasChanges);
      
      return updated;
    });
  };

  const selectAllScreensForModule = (moduleId, enable = true) => {
    const module = menuData.find(m => (m.moduleId || m.module_id) === moduleId);
    if (!module) return;

    const subMenus = module.subMenu || module.sub_menu || [];
    setScreenMappings(prev => {
      const updated = { ...prev };
      subMenus.forEach(screen => {
        const screenId = screen.screen_id || screen.screenId;
        if (screenId) {
          updated[screenId] = {
            ...(updated[screenId] || {}),
            isActive: enable,
          };
        }
      });
      
      // Check if there are changes
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(initialMappings);
      setHasChanges(hasChanges);
      
      return updated;
    });
  };

  const handleSave = async () => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID is required');
      return;
    }

    setSaving(true);
    try {
      const orgId = tenant.tenantId;

      // Prepare save/update operations for all screens
      const savePromises = Object.entries(screenMappings).map(async ([screenId, mapping]) => {
        const screenIdInt = parseInt(screenId);
        const payload = {
          orgId: orgId,
          moduleId: mapping.moduleId,
          featureScreenId: screenIdInt,
          type: 'S',
          isActive: mapping.isActive
        };

        if (mapping.mappingId) {
          // Update existing mapping
          try {
            await apiRequest(
              `/gateway/api/ticket/tenant-mapping/${mapping.mappingId}`,
              {
                method: 'PUT',
                body: payload
              }
            );
            return { success: true, screenId: screenIdInt };
          } catch (error) {
            console.error(`Error updating mapping for screen ${screenIdInt}:`, error);
            throw error;
          }
        } else if (mapping.isActive) {
          // Create new mapping only if isActive is true
          try {
            const response = await apiRequest(
              `/gateway/api/ticket/tenant-mapping/save`,
              {
                method: 'POST',
                body: payload
              }
            );
            // Extract mapping ID from response for future updates
            const mappingId = response?.data?.id || response?.id;
            if (mappingId) {
              setScreenMappings(prev => ({
                ...prev,
                [screenId]: { ...prev[screenId], mappingId: mappingId }
              }));
            }
            return { success: true, screenId: screenIdInt };
          } catch (error) {
            // If duplicate constraint error, fetch and update existing mapping
            if (error.response?.status === 400 || error.response?.data?.message?.includes('already exists')) {
              console.warn(`Mapping already exists for screen ${screenIdInt}, fetching to update...`);
              try {
                const mappingsResponse = await apiRequest(
                  `/gateway/api/ticket/tenant-mapping/org/${orgId}/type/S`,
                  { method: 'GET' }
                );
                
                let mappings = [];
                if (Array.isArray(mappingsResponse)) {
                  mappings = mappingsResponse;
                } else if (mappingsResponse?.data && Array.isArray(mappingsResponse.data)) {
                  mappings = mappingsResponse.data;
                }
                
                const existingMapping = mappings.find(m => (m.featureScreenId || m.feature_screen_id) === screenIdInt);
                if (existingMapping?.id) {
                  await apiRequest(
                    `/gateway/api/ticket/tenant-mapping/${existingMapping.id}`,
                    {
                      method: 'PUT',
                      body: payload
                    }
                  );
                  setScreenMappings(prev => ({
                    ...prev,
                    [screenId]: { ...prev[screenId], mappingId: existingMapping.id }
                  }));
                  return { success: true, screenId: screenIdInt };
                }
              } catch (fetchError) {
                console.error(`Error fetching existing mapping for screen ${screenIdInt}:`, fetchError);
              }
            }
            throw error;
          }
        }
        // If isActive is false and no mapping exists, nothing to do
        return { success: true, screenId: screenIdInt };
      });

      await Promise.all(savePromises);

      //console.log('✅ Screen mappings saved successfully');

      toast.success('Permissions saved successfully');
      setInitialMappings(JSON.parse(JSON.stringify(screenMappings))); // Update initial state
      setHasChanges(false);
      
      // Refresh the data to get updated mappings
      await fetchTenantPermissions();
    } catch (error) {
      console.error('Error saving permissions:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save permissions';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getScreenActive = (screenId) => {
    return screenMappings[screenId]?.isActive || false;
  };

  if (!isOpen) return null;

  const modalFooter = (
    <div className="flex items-center justify-end gap-3">
      <Button
        variant="outline"
        onClick={onClose}
        disabled={saving}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        disabled={saving || !hasChanges}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="h-4 w-4 mr-2" />
            Save Permissions
          </>
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tenant Screen Permissions"
      size="xl"
      footer={modalFooter}
    >
      {/* Header Subtitle */}
      {tenant && (
        <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{tenant.tenantName || 'Tenant'}</span> (ID: {tenant.tenantId})
          </p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading permissions...</p>
        </div>
      ) : menuData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No permissions found</p>
          <p className="text-gray-500 text-sm">
            This tenant may not have any screen permissions configured.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {menuData.map((module) => {
            const moduleId = module.moduleId || module.module_id;
            const moduleName = module.moduleCode || module.moduleDesc || module.module_code || module.module_desc || 'Unknown Module';
            const subMenus = module.subMenu || module.sub_menu || [];
            const isExpanded = expandedModules.has(moduleId);
            const moduleScreens = subMenus
              .map(screen => screenMappings[screen.screen_id || screen.screenId])
              .filter(Boolean);
            const allSelected = moduleScreens.length > 0 && 
              moduleScreens.every(m => m.isActive);
            const noneSelected = moduleScreens.length === 0 || 
              moduleScreens.every(m => !m.isActive);

            return (
              <div 
                key={moduleId} 
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Module Header */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700">
                  <button
                    onClick={() => toggleModule(moduleId)}
                    className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {moduleName}
                    </h3>
                    <Badge className="bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300">
                      {subMenus.length} screen{subMenus.length !== 1 ? 's' : ''}
                    </Badge>
                  </button>
                  
                  {/* Select All Button */}
                  {isExpanded && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllScreensForModule(moduleId, !allSelected);
                      }}
                      className="ml-2"
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>

                {/* Screens List */}
                {isExpanded && (
                  <div className="bg-white dark:bg-gray-800">
                    {subMenus.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        No screens available
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {subMenus.map((screen, index) => {
                          const screenId = screen.screen_id || screen.screenId;
                          const screenName = screen.screenLabel || screen.screen_label || screen.screenName || screen.screen_name || 'Unknown Screen';
                          const isActive = getScreenActive(screenId);

                          return (
                            <div 
                              key={screenId || index}
                              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                  {screenName}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={isActive}
                                    onChange={(checked) => toggleScreen(screenId, checked)}
                                  />
                                  {isActive && (
                                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default TenantPermissionsModal;
